/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import axios from "axios";
import Modal from "react-bootstrap/Modal";
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  AppAlert,
  Instance,
  isAndroid,
  isChrome,
  isIOSChrome,
  isIPhone,
  isSamsungBrowser,
  isSafariBrowser,
  ReportViewer,
  IErrorMessage,
} from "@advapacs/common";
import IconClose from "@src/assets/icons/close-white.svg";
import InfoWhiteIcon from "@src/assets/icons/info-white.svg";
import { ViewerButtonType } from "@advapacs/common/src/constant";
import { useSelector } from "react-redux";
import { IRootState } from "@src/config/reducers";
import { getMobileOperatingSystem } from "@src/utils/utils";
import { ViewerFooter } from "./ViewerFooter";
import { ViewerMobileFooter } from "./ViewerBodyFooter";
import { v4 as uuid } from "uuid";
import { useFlag } from "@unleash/proxy-client-react";
import { FrameNavigator } from "./FrameNavigator";
import {
  DownloadAsMenuData,
  DownloadAsOption,
  getDownloadAsUrl,
  getImageInstances,
  hasImage,
  isMultiframe,
} from "@src/utils/downloadAs";

// Delay the loading state so Share does not flash.
const IMAGE_LOADING_DELAY_MS = 300;

export interface ImagesViewerModalProps {
  src: string;
  name: string;
  id: string;
  dataSrc?: string;
  numberOfFrames?: number;
}

export interface ViewerProps {
  show: boolean;
  seriesImagesViewerModal: Array<ImagesViewerModalProps>;
  onClose: () => void;
  title: string;
  studyId?: string;
  seriesId?: string;
  currentSeriesIndex?: number;
  downloadInstance?: (
    studyId?: string,
    seriesId?: string,
    instanceId?: string,
    frame?: number
  ) => void;
  downloadByUrl?: (url: string) => void;
}
const ViewerModal: FC<ViewerProps> = ({
  show,
  onClose,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  title,
  seriesImagesViewerModal,
  studyId,
  seriesId,
  currentSeriesIndex = -1,
  downloadInstance,
  downloadByUrl,
}) => {
  const downloadAsEnabled = useFlag("pp-download-multiframe-as");
  const reportViewerRef = useRef(null);
  const { isMobileOnly, isMobile , isTablet} = useSelector(
    (state: IRootState) => state.responsiveReducer
  );
  const [loading, setLoading] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const instanceImagesViewerSize = useRef(0);
  const [indexInstanceImage, setIndexInstanceImage] = useState(0);
  const [instanceImagesViewerModal, setInstanceImagesViewerModal] = useState<
    Array<ImagesViewerModalProps>
  >([]);
  const [indexSeriesImage, setIndexSeriesImage] = useState(
    currentSeriesIndex - 1
  );
  const [currentSeriesId, setCurrentSeriesId] = useState(seriesId);

  const getInstanceImages = async (sId: string) => {
    try {
      setLoading(true);
      const instanceIds = await axios.get(
        `/studies/${studyId}/series/${sId}/instances?size=1000000`
      );
      // Non-image instances are left out (unless the series has no image).
      const content: Instance[] = instanceIds.data.content || [];
      const instances = downloadAsEnabled
        ? getImageInstances(content)
        : content;
      setInstanceImagesViewerModal(
        instances.map((instance: Instance) => {
          return {
            src: `/studies/${studyId}/series/${sId}/instances/${instance.id}/render?quality=80&frame=1`,
            name: instance.sopInstanceUid,
            id: instance.id,
            numberOfFrames: instance.numberOfFrames,
          };
        })
      );
      instanceImagesViewerSize.current = instances.length;
      setLoading(false);
      setIndexInstanceImage(0);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (studyId && currentSeriesId) getInstanceImages(currentSeriesId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studyId, currentSeriesId]);

  const currentSeriesImageId = seriesImagesViewerModal[indexSeriesImage]?.id;
  const currentInstance = instanceImagesViewerModal[indexInstanceImage];
  const currentFrames = currentInstance?.numberOfFrames ?? 0;
  const frameNavigable = downloadAsEnabled && isMultiframe(currentFrames);
  const [frameState, setFrameState] = useState({ instanceId: "", frame: 1 });
  // Another instance falls back to frame 1.
  const currentFrame =
    frameNavigable && frameState.instanceId === currentInstance?.id
      ? Math.min(frameState.frame, currentFrames)
      : 1;

  const frameOf = (state: { instanceId: string; frame: number }) =>
    state.instanceId === currentInstance?.id ? state.frame : 1;
  const handleNextFrame = () =>
    setFrameState((prev) => ({
      instanceId: currentInstance?.id ?? "",
      frame: Math.min(frameOf(prev) + 1, currentFrames),
    }));
  const handlePreviousFrame = () =>
    setFrameState((prev) => ({
      instanceId: currentInstance?.id ?? "",
      frame: Math.max(frameOf(prev) - 1, 1),
    }));

  const imageLoadingTimer = useRef<ReturnType<typeof setTimeout>>();
  const handleDelayedImageLoading = useCallback((isLoading: boolean) => {
    clearTimeout(imageLoadingTimer.current);
    if (isLoading) {
      imageLoadingTimer.current = setTimeout(
        () => setImageLoading(true),
        IMAGE_LOADING_DELAY_MS
      );
    } else {
      setImageLoading(false);
    }
  }, []);
  useEffect(() => () => clearTimeout(imageLoadingTimer.current), []);

  // Desktop: overlay on the image area. Tablet/mobile: above the tools row.
  const frameNavigator = frameNavigable ? (
    <FrameNavigator
      frame={currentFrame}
      frames={currentFrames}
      onPrevious={handlePreviousFrame}
      onNext={handleNextFrame}
    />
  ) : null;

  const handleDownloadAs = (option: DownloadAsOption) => {
    if (
      !downloadByUrl ||
      !studyId ||
      !currentSeriesImageId ||
      !currentInstance?.id
    )
      return;
    downloadByUrl(
      getDownloadAsUrl({
        studyId,
        seriesId: currentSeriesImageId,
        instanceId: currentInstance.id,
        option,
        frames: currentInstance.numberOfFrames ?? 0,
        frame: currentFrame,
      })
    );
  };
  const anyOtherMultiframe = instanceImagesViewerModal.some(
    (instance, index) =>
      index !== indexInstanceImage && isMultiframe(instance.numberOfFrames)
  );
  const downloadAsMenu: DownloadAsMenuData | undefined =
    downloadAsEnabled &&
    !loading &&
    downloadByUrl &&
    studyId &&
    currentSeriesImageId &&
    currentInstance?.id
      ? {
          imageCount: instanceImagesViewerModal.filter((instance) =>
            hasImage(instance.numberOfFrames)
          ).length,
          frames: currentInstance.numberOfFrames ?? 0,
          anyOtherMultiframe,
          onSelect: handleDownloadAs,
        }
      : undefined;

  const mobileLayout = useMemo(() => {
    return isMobile || isTablet;
  }, [isMobile, isTablet]);

  const showPrevInstance = () => {
    setIndexInstanceImage((prevState) => {
      if (prevState === 0) {
        return 0;
      }
      return prevState - 1;
    });
  };
  const showNextInstance = () => {
    setIndexInstanceImage((prevState) => {
      if (prevState === instanceImagesViewerSize.current - 1) {
        return prevState;
      }
      return prevState + 1;
    });
  };

  const onCloseModal = () => {
    setIndexInstanceImage(0);
    onClose();
  };

  const selectedInstance = useMemo(() => {
    return instanceImagesViewerModal[indexInstanceImage];
  }, [indexInstanceImage, instanceImagesViewerModal]);

  const viewerSrc = frameNavigable
    ? (selectedInstance?.src ?? "").replace(
        /frame=\d+/,
        `frame=${currentFrame}`
      )
    : selectedInstance?.src || "";
  // Cache key per frame.
  const viewerInstanceId = frameNavigable
    ? `${selectedInstance?.id}:${currentFrame}`
    : selectedInstance?.id;

  const handleNext = () => {
    // clear cached
    sessionStorage.removeItem("cached_instances");
    if (indexSeriesImage < seriesImagesViewerModal.length) {
      setInstanceImagesViewerModal([]);
      setIndexInstanceImage(0);
      instanceImagesViewerSize.current = 0;
      setIndexSeriesImage(indexSeriesImage + 1);
      setCurrentSeriesId(seriesImagesViewerModal[indexSeriesImage + 1]?.id);
    }
  };

  const handleBack = () => {
    // clear cached
    sessionStorage.removeItem("cached_instances");
    if (indexSeriesImage > 0) {
      setInstanceImagesViewerModal([]);
      setIndexInstanceImage(0);
      instanceImagesViewerSize.current = 0;
      setIndexSeriesImage(indexSeriesImage - 1);
      setCurrentSeriesId(seriesImagesViewerModal[indexSeriesImage - 1]?.id);
    }
  };

  const handleIconClick = (buttonType: ViewerButtonType) => {
    if (reportViewerRef.current) {
      // @ts-ignore
      reportViewerRef.current?.handleAction(buttonType);
    }
  };

  const isTabletOnly = useMemo(() => {
    return !isMobileOnly && isMobile;
  }, [isMobile, isMobileOnly]);

  const device = getMobileOperatingSystem();

  const expandBodyClass = useMemo(() => {
    if (device === "IOS") return "ios";

    if (device === "AND") return "android";

    return "";
  }, [device]);

  const handleShareImageInstance = async () => {
    try {
      // @ts-ignore
      const image = await reportViewerRef?.current?.getImgBase64();
      const fileName = (selectedInstance?.name || uuid()) + ".jpg";
      const file = image
        ? new File([image], fileName, { type: image.type })
        : null;
      // @ts-ignore
      if (file && navigator.canShare) {
        await navigator.share({
          title: fileName,
          text: fileName,
          files: [file],
        });
      } else {
        setErrorMessage("Unable to share this image");
      }
    } catch (err) {
      if (!((err as IErrorMessage)?.message?.indexOf("Abort") > -1))
        setErrorMessage((err as IErrorMessage)?.message);
    }
  };

  useEffect(() => {
    // clear cached
    sessionStorage.removeItem("cached_instances");
  }, []);

  const isAndroidChrome = () => {
    return isAndroid() && isChrome() && !isSamsungBrowser();
  };

  const isIphoneChrome = () => {
    return isIPhone() && isIOSChrome();
  };

  const mobileClass = () => {
    if (isIphoneChrome()) return "ip-chrome";
    if (isAndroidChrome()) return "android-chrome";
    if (isSafariBrowser()) return "safari";
    return "normal";
  };

  return (
    <>
      <Modal
        show={show}
        className="lading-viewer-modal"
        onHide={onClose}
        dialogClassName={`landing-full-modal-dialog ${mobileClass()}`}
      >
        <Modal.Header
          className="d-flex justify-content-between align-items-center p-tb-xxs p-r-none"
          onHide={() => onCloseModal()}
        >
          <span>{seriesImagesViewerModal[indexSeriesImage]?.name}</span>
          <button className="btn" onClick={() => onCloseModal()}>
            <img className="cursor-pointer" src={IconClose} alt="Close Icon" />
          </button>
        </Modal.Header>
        <Modal.Body className="p-none">
          <div className="viewer-warning row p-lr-md p-tb-xxs">
            <img
              className="m-r-xxs padding-b-3px"
              src={InfoWhiteIcon}
              alt="Info White Icon"
            />

            <span className="text-white">
              Images are not for diagnostic purposes
            </span>
          </div>
          {errorMessage && (
            <AppAlert.ErrorAlert
              error={errorMessage}
              dismissible
              onClose={() => setErrorMessage("")}
              className="mt-1"
            />
          )}
          <div className="row view-documents-holder landing ">
            <div className="flex-1 position-relative">
              <ReportViewer
                ref={reportViewerRef}
                src={viewerSrc}
                showToolBarButton={false}
                setImageLoadingProp={
                  downloadAsEnabled
                    ? handleDelayedImageLoading
                    : setImageLoading
                }
                showPrevInstance={
                  frameNavigable ? handlePreviousFrame : showPrevInstance
                }
                showNextInstance={
                  frameNavigable ? handleNextFrame : showNextInstance
                }
                // cached images
                instanceId={viewerInstanceId}
              />
              {!mobileLayout && frameNavigator}
            </div>
            {mobileLayout && (
              <ViewerMobileFooter
                isMobileOnly={isMobile}
                isTabletOnly={isTablet}
                loading={loading}
                imgLoading={imageLoading}
                indexInstanceImage={indexInstanceImage}
                indexSeriesImage={indexSeriesImage}
                seriesImagesViewerModal={seriesImagesViewerModal}
                instanceImagesViewerModal={instanceImagesViewerModal}
                downloadAsMenu={downloadAsMenu}
                frameNavigator={frameNavigator}
                reserveFrameNavigator={downloadAsEnabled}
                downloadInstance={() => {
                  downloadInstance?.(
                    studyId,
                    seriesImagesViewerModal[indexSeriesImage]?.id,
                    instanceImagesViewerModal[indexInstanceImage]?.id,
                    1
                  );
                }}
                expandBodyClass={expandBodyClass}
                handleIconClick={handleIconClick}
                shareImageInstance={handleShareImageInstance}
              />
            )}
          </div>
        </Modal.Body>
        <Modal.Footer
          className={`modal-footer d-flex ${
            mobileLayout ? "justify-content-center" : "justify-content-between"
          } align-items-center pp-model-footer`}
        >
          <ViewerFooter
            loading={loading}
            imgLoading={imageLoading}
            isTabletOnly={isTabletOnly}
            mobileLayout={mobileLayout}
            indexInstanceImage={indexInstanceImage}
            indexSeriesImage={indexSeriesImage}
            seriesImagesViewerModal={seriesImagesViewerModal}
            instanceImagesViewerModal={instanceImagesViewerModal}
            handleBack={handleBack}
            handleNext={handleNext}
            handleIconClick={handleIconClick}
            showNextInstance={showNextInstance}
            showPrevInstance={showPrevInstance}
            downloadAsMenu={downloadAsMenu}
            downloadInstance={() => {
              downloadInstance?.(
                studyId,
                seriesImagesViewerModal[indexSeriesImage]?.id,
                instanceImagesViewerModal[indexInstanceImage]?.id,
                1
              );
            }}
            handleShareImageInstance={handleShareImageInstance}
          />
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ViewerModal;
